import * as React from "react";
import { Checkbox, Chip, FormControl, InputLabel, ListSubheader, MenuItem, Select, TextField } from "@mui/material";
import { TemplateGroup, TemplateTask } from "../../../../../../models/basic/template";
import { KeyboardArrowDown } from "@mui/icons-material";
import { translate } from "../../../../../../translate/translate";

type PredecessorSelectProps = {
  currentTaskId: number;
  groups: TemplateGroup[];
  value: TemplateTask["predecessor"];
  onChange: (value: number[]) => void;
  fullWidth?: boolean;
  label?: string;
  disabled?: boolean;
};

type VisibleGroupEntry = {
  group: TemplateGroup;
  depth: number;
  tasks: TemplateTask[];
};

const MENU_MAX_HEIGHT = 420;
export const MENU_MIN_HEIGHT = 240;
const VIEWPORT_MARGIN = 16;
const MAX_INDENT_DEPTH = 3;

const getIndent = (depth: number) => 2 + Math.min(depth, MAX_INDENT_DEPTH);

export const getPredecessorIds = (predecessor: TemplateTask["predecessor"]): number[] => {
  if (Array.isArray(predecessor)) {
    return predecessor.filter((item): item is number => typeof item === "number");
  }

  return typeof predecessor === "number" ? [predecessor] : [];
};

const getTaskLabel = (task: TemplateTask) => task.acronym || task.name;

const normalizeText = (text: string) => (
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase()
);

const buildVisibleEntries = (
  groups: TemplateGroup[],
  parentId: number | null,
  depth: number,
  isTaskVisible: (task: TemplateTask) => boolean
): VisibleGroupEntry[] => {
  const siblings = groups
    .filter((group) => (group.ancestor_group ?? null) === parentId)
    .sort((groupA, groupB) => (groupA.order ?? 0) - (groupB.order ?? 0));

  return siblings.flatMap((group) => {
    const tasks = group.tasks.filter(isTaskVisible);
    const descendantEntries = buildVisibleEntries(groups, group.id, depth + 1, isTaskVisible);

    if (tasks.length === 0 && descendantEntries.length === 0) {
      return [];
    }

    return [{ group, depth, tasks }, ...descendantEntries];
  });
};

const PredecessorSelectComponent = ({
  currentTaskId,
  groups,
  value,
  onChange,
  fullWidth,
  label,
  disabled = false,
}: PredecessorSelectProps) => {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = React.useState(MENU_MAX_HEIGHT);
  const [menuWidth, setMenuWidth] = React.useState(0);
  const fieldRef = React.useRef<HTMLDivElement | null>(null);

  const selectedIds = getPredecessorIds(value).filter((id) => id !== currentTaskId);
  const normalizedSearch = normalizeText(search);
  const selectableTaskMap = new Map<number, TemplateTask>();

  groups.forEach((group) => {
    group.tasks.forEach((task) => {
      if (task.id !== currentTaskId) {
        selectableTaskMap.set(task.id, task);
      }
    });
  });

  const isTaskVisible = (task: TemplateTask) => {
    if (task.id === currentTaskId) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return normalizeText(task.name).includes(normalizedSearch)
      || normalizeText(task.acronym).includes(normalizedSearch);
  };

  const visibleEntries = buildVisibleEntries(groups, null, 0, isTaskVisible);

  const getAvailableHeight = () => {
    const fieldBottom = fieldRef.current?.getBoundingClientRect().bottom ?? 0;
    return window.innerHeight - fieldBottom - VIEWPORT_MARGIN;
  };

  const handleOpen = () => {
    if (!fieldRef.current) {
      return;
    }

    if (getAvailableHeight() < MENU_MIN_HEIGHT) {
      fieldRef.current.scrollIntoView({ block: "center" });
    }

    setMenuWidth(fieldRef.current.offsetWidth);
    setMenuMaxHeight(Math.min(MENU_MAX_HEIGHT, getAvailableHeight()));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  const handleSelectChange = (event: any) => {
    const nextValue = typeof event.target.value === "string"
      ? event.target.value.split(",")
      : event.target.value as Array<number | string>;

    if (nextValue.includes("")) {
      onChange([]);
      return;
    }

    onChange(nextValue.map(Number).filter((item: any) => !Number.isNaN(item)));
  };

  const renderSelectedValue = (selected: unknown) => {
    const ids = (selected as number[]).filter((item) => selectableTaskMap.has(item));

    if (!ids.length) {
      return <span className="npi-template-predecessor-placeholder">{translate.t("no_predecessor")}</span>;
    }

    return ids.map((item) => getTaskLabel(selectableTaskMap.get(item)!!)).join(", ");
  };

  return (
    <FormControl
      ref={fieldRef}
      size="small"
      fullWidth={fullWidth}
      className="npi-template-field"
      disabled={disabled}
      sx={{ width: "100%", minWidth: 0 }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        multiple
        displayEmpty
        sx={{ width: "100%" }}
        disabled={disabled}
        label={label}
        value={selectedIds}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleSelectChange}
        renderValue={renderSelectedValue}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          marginThreshold: null,
          PaperProps: {
            className: "npi-template-predecessor-menu",
            sx: {
              width: menuWidth,
              minWidth: menuWidth,
              maxWidth: menuWidth,
              maxHeight: menuMaxHeight,
              overflowX: "auto",
            },
          },
          MenuListProps: { dense: true, sx: { width: "max-content", minWidth: "100%" } },
        }}
      >
        <ListSubheader className="npi-template-predecessor-search">
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={translate.t("search_task")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          />
        </ListSubheader>
        <MenuItem value="" className="npi-template-predecessor-empty" onClick={handleClose}>
          {translate.t("no_predecessor")}
        </MenuItem>
        {visibleEntries.flatMap(({ group, depth, tasks }) => [
          <ListSubheader key={`group-${group.id}`} disableSticky className="npi-template-predecessor-group" sx={{ pl: getIndent(depth) }}>
            <KeyboardArrowDown fontSize="small" />
            {group.name}
          </ListSubheader>,
          ...tasks.map((task) => (
            <MenuItem
              key={task.id}
              value={task.id}
              className="npi-template-predecessor-option"
              sx={{ pl: getIndent(depth) }}>
              <Checkbox size="small" checked={selectedIds.includes(task.id)} />
              <span className="npi-template-predecessor-option-name">
                <span>{task.name}</span>
                <Chip
                  label={task.acronym}
                  size="small"
                  color="primary"
                  sx={{ marginLeft: "8px", flexShrink: 0, maxWidth: "none", borderRadius: "8px", backgroundColor: "#EAEEFF", color: "#331EBE", border: "1px solid #331EBE" }}/>
              </span>
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  );
};

export default PredecessorSelectComponent;
